import { useState } from "react";

import { aFila, revisar, soloDigitos } from "../../shared/altaChofer";
import { existeChofer, personaDelPadron } from "../../services/documentacion";
import { LARGA, MOV } from "../../shared/tipoServicio";

/* Dar de alta un chofer sin salir de la pantalla.
 *
 * ── EL DNI VA PRIMERO Y ES EL QUE DISPARA TODO ───────────────────────────
 *
 * Al salir de ese campo se hacen las dos preguntas que sólo la base puede
 * contestar: **¿ya está?** y **¿quién es según el padrón?**
 *
 * La primera FRENA —guardar sobre un DNI que existe pisa esa fila y se pierden
 * los vencimientos cargados— y la segunda **ofrece el nombre**. Es el cotejo
 * del alta de movimientos: el nombre ya está escrito en el padrón y tipearlo de
 * nuevo es la forma de que las dos versiones difieran.
 *
 * **Y sólo completa el campo vacío.** Si alguien ya escribió un nombre, no se
 * lo pisa: lo automático completa huecos, la persona corrige cualquier cosa.
 *
 * ── NO ESTAR EN EL PADRÓN NO ES UN ERROR ─────────────────────────────────
 *
 * Son 13 DNI reales con papeles cargados y sin fila en `personas`. Se dice y se
 * sigue — tratarlo como error obligaría a inventar una fila del padrón para
 * poder cargar un vencimiento.
 *
 * ── SÓLO DNI Y NOMBRE SON OBLIGATORIOS ───────────────────────────────────
 *
 * El resto se completa después, desde la misma tabla. Un alta que pide ocho
 * campos es un alta que se hace en otro momento — y «en otro momento» es
 * nunca, que es exactamente el problema que esto viene a resolver.
 */
const AltaChofer = ({ onCreado, onCancelar, guardar }) => {
  const [f, setF] = useState({
    dni: "", nombre: "", empresa: "", telefono: "", zona: "",
    registro_vence: "", tipo_servicio: "",
  });
  const [yaExiste, setYaExiste] = useState(false);
  const [enPadron, setEnPadron] = useState(null);
  const [mirando, setMirando] = useState(false);
  const [noSePudo, setNoSePudo] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState("");

  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));

  const mirarDni = async () => {
    const d = soloDigitos(f.dni);
    setYaExiste(false);
    setEnPadron(null);
    setNoSePudo(false);
    if (!d) return;
    setMirando(true);
    try {
      const [existe, persona] = await Promise.all([
        existeChofer(d),
        personaDelPadron(d),
      ]);
      // `null` es «no pude fijarme», que NO es «está limpio».
      if (existe === null) setNoSePudo(true);
      else setYaExiste(existe);
      setEnPadron(Boolean(persona));
      // Sólo llena el hueco: si ya escribieron un nombre, no se pisa.
      if (persona?.nombre) {
        setF((x) => (x.nombre.trim() ? x : { ...x, nombre: persona.nombre }));
      }
    } finally {
      setMirando(false);
    }
  };

  const r = revisar(f, { yaExiste, enPadron });

  const enviar = async (e) => {
    e.preventDefault();
    if (!r.ok || guardando) return;
    setGuardando(true);
    setError("");
    try {
      await guardar(aFila(f));
      onCreado(aFila(f));
    } catch (err) {
      setError(err?.message || "No se pudo guardar.");
    } finally {
      setGuardando(false);
    }
  };

  return (
    <form className="alta-chofer" onSubmit={enviar}>
      <h3>Agregar un chofer</h3>

      <div className="ac-campos">
        <label>
          DNI
          <input
            value={f.dni}
            onChange={set("dni")}
            onBlur={mirarDni}
            inputMode="numeric"
            autoFocus
            placeholder="30123456"
          />
        </label>
        <label>
          Nombre
          <input
            value={f.nombre}
            onChange={set("nombre")}
            placeholder="APELLIDO, Nombre"
          />
        </label>
        <label>
          Empresa
          <input value={f.empresa} onChange={set("empresa")} placeholder="TC" />
        </label>
        <label>
          Teléfono
          <input
            value={f.telefono}
            onChange={set("telefono")}
            placeholder="11 5555 5555"
          />
        </label>
        <label>
          Zona
          <input value={f.zona} onChange={set("zona")} placeholder="TIGRE" />
        </label>
        <label>
          Vence el registro
          <input
            type="date"
            value={f.registro_vence}
            onChange={set("registro_vence")}
          />
        </label>
        <label>
          Servicio
          <select value={f.tipo_servicio} onChange={set("tipo_servicio")}>
            <option value="">Sin dato</option>
            <option value={LARGA}>Larga distancia</option>
            <option value={MOV}>Movimientos</option>
          </select>
        </label>
      </div>

      {mirando && <p className="ac-nota">Fijándome si ya está…</p>}

      {/* «No pude fijarme» no se dice como «está limpio». */}
      {noSePudo && (
        <p className="ac-aviso">
          No pude verificar si ese DNI ya está cargado. Se puede guardar igual,
          pero conviene buscarlo primero en la lista.
        </p>
      )}

      {r.errores.map((x) => (
        <p className="ac-error" key={x}>{x}</p>
      ))}
      {r.avisos.map((x) => (
        <p className="ac-aviso" key={x}>{x}</p>
      ))}
      {error && <p className="ac-error">{error}</p>}

      <div className="ac-acciones">
        <button type="submit" className="btn-primario"
                disabled={!r.ok || guardando}>
          {guardando ? "Guardando…" : "Agregar"}
        </button>
        <button type="button" className="btn-suave" onClick={onCancelar}
                disabled={guardando}>
          Cancelar
        </button>
        {/* El resto se completa después desde la misma tabla: un alta que pide
            ocho campos se hace «en otro momento», y en otro momento es nunca. */}
        <span className="ac-nota">
          Sólo el DNI y el nombre son obligatorios.
        </span>
      </div>
    </form>
  );
};

export default AltaChofer;
